import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { publicQuotesApi, type PublicOrderData } from '@/services/public-quotes';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const OrderTrackingPage = () => {
  const { user, isAuthenticated } = useAuth();
  const [emailInput, setEmailInput] = useState('');
  const [searchEmail, setSearchEmail] = useState<string | null>(null);

  const isEmailValid = EMAIL_REGEX.test(emailInput);

  const {
    data: orders,
    isLoading,
    error,
    isFetched,
  } = useQuery({
    queryKey: ['public-order-lookup', searchEmail],
    queryFn: () => publicQuotesApi.lookupOrders(searchEmail!),
    enabled: !!searchEmail,
    staleTime: 60_000,
    retry: false,
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (isEmailValid) {
        setSearchEmail(emailInput.toLowerCase().trim());
      }
    },
    [emailInput, isEmailValid],
  );

  // Pre-fill email for logged-in users
  const handleAutoFill = useCallback(() => {
    if (user?.email) {
      setEmailInput(user.email);
      setSearchEmail(user.email.toLowerCase().trim());
    }
  }, [user]);

  return (
    <div
      className="container-main"
      style={{
        maxWidth: '700px',
        paddingTop: 'var(--space-12)',
        paddingBottom: 'var(--space-12)',
      }}
    >
      <h1
        style={{
          fontSize: 'var(--text-3xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--text-primary)',
          letterSpacing: 'var(--tracking-tight)',
          marginBottom: 'var(--space-2)',
        }}
      >
        Track Your Order
      </h1>
      <p
        style={{
          color: 'var(--text-secondary)',
          marginBottom: 'var(--space-8)',
        }}
      >
        Enter the email address you used during checkout to view your orders.
      </p>

      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-8)',
        }}
      >
        <input
          type="email"
          value={emailInput}
          onChange={(e) => {
            setEmailInput(e.target.value);
            setSearchEmail(null);
          }}
          placeholder="your@email.com"
          style={{
            flex: 1,
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-default)',
            padding: 'var(--space-2) var(--space-3)',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-primary)',
            backgroundColor: 'var(--bg-surface)',
            minHeight: '42px',
          }}
        />
        <Button
          type="submit"
          disabled={!isEmailValid || isLoading}
          size="md"
        >
          {isLoading ? 'Searching...' : 'Look Up'}
        </Button>
      </form>

      {isAuthenticated && !searchEmail && (
        <button
          type="button"
          onClick={handleAutoFill}
          style={{
            display: 'block',
            marginTop: 'calc(-1 * var(--space-6))',
            marginBottom: 'var(--space-6)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-primary-600)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: 0,
          }}
        >
          Use my account email ({user?.email})
        </button>
      )}

      {error && (
        <div
          style={{
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'var(--bg-error-subtle)',
            padding: 'var(--space-4)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-error-700)',
            marginBottom: 'var(--space-6)',
          }}
        >
          {error instanceof Error ? error.message : 'Something went wrong'}
        </div>
      )}

      {isLoading && (
        <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
          <LoadingSpinner size="lg" />
        </div>
      )}

      {isFetched && !isLoading && orders && orders.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: 'var(--space-8)',
            borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--border-default)',
            backgroundColor: 'var(--bg-surface)',
          }}
        >
          <p
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--text-secondary)',
            }}
          >
            No orders found for this email address.
          </p>
        </div>
      )}

      {orders && orders.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-4)',
          }}
        >
          {orders.map((order) => (
            <OrderCard key={order.orderId} order={order} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ---- helpers ---- */

const STATUS_LABELS: Readonly<Record<string, string>> = {
  paid: 'Paid',
  queued: 'Queued',
  printing: 'Printing',
  post_processing: 'Post-Processing',
  packed: 'Packed',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
  failed: 'Failed',
};

const STATUS_COLORS: Readonly<Record<string, { bg: string; text: string }>> = {
  paid: { bg: 'var(--bg-success-subtle)', text: 'var(--color-success-700)' },
  queued: { bg: 'var(--bg-warning-subtle)', text: 'var(--color-warning-700)' },
  printing: { bg: 'var(--bg-info-subtle)', text: 'var(--color-info-700)' },
  post_processing: { bg: 'var(--bg-info-subtle)', text: 'var(--color-info-700)' },
  packed: { bg: 'var(--bg-info-subtle)', text: 'var(--color-info-700)' },
  shipped: { bg: 'var(--bg-success-subtle)', text: 'var(--color-success-700)' },
  delivered: { bg: 'var(--bg-success-subtle)', text: 'var(--color-success-700)' },
  cancelled: { bg: 'var(--bg-error-subtle)', text: 'var(--color-error-700)' },
  refunded: { bg: 'var(--bg-error-subtle)', text: 'var(--color-error-700)' },
  failed: { bg: 'var(--bg-error-subtle)', text: 'var(--color-error-700)' },
};

const OrderCard = ({ order }: { readonly order: PublicOrderData }) => {
  const statusLabel = STATUS_LABELS[order.status] ?? order.status;
  const statusColor = STATUS_COLORS[order.status] ?? {
    bg: 'var(--bg-surface-sunken)',
    text: 'var(--text-secondary)',
  };
  const createdDate = new Date(order.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div
      style={{
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--border-default)',
        backgroundColor: 'var(--bg-surface)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        className="flex justify-between items-center"
        style={{
          padding: 'var(--space-4) var(--space-5)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <div>
          <p
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--text-primary)',
            }}
          >
            {order.productName ?? 'Order'}
          </p>
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-tertiary)',
              marginTop: '2px',
            }}
          >
            {createdDate}
          </p>
        </div>
        <span
          style={{
            padding: 'var(--space-1) var(--space-3)',
            borderRadius: 'var(--radius-full)',
            backgroundColor: statusColor.bg,
            color: statusColor.text,
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-semibold)',
          }}
        >
          {statusLabel}
        </span>
      </div>

      {/* Body */}
      <div
        style={{
          padding: 'var(--space-4) var(--space-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
        }}
      >
        <div className="grid grid-cols-2 sm:grid-cols-4" style={{ gap: 'var(--space-3)' }}>
          {order.materialId && (
            <DetailItem label="Material" value={order.materialId.toUpperCase()} />
          )}
          {order.color && (
            <DetailItem label="Color" value={capitalize(order.color)} />
          )}
          <DetailItem label="Qty" value={String(order.quantity)} />
          <DetailItem
            label="Total"
            value={`$${order.totalPrice.toFixed(2)}`}
          />
        </div>

        {/* Tracking */}
        {order.trackingNumber && (
          <div
            style={{
              borderRadius: 'var(--radius-lg)',
              backgroundColor: 'var(--bg-surface-sunken)',
              padding: 'var(--space-3)',
            }}
          >
            <p
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: '2px',
              }}
            >
              Tracking
            </p>
            <p
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text-primary)',
              }}
            >
              {order.trackingCarrier ? `${order.trackingCarrier}: ` : ''}
              {order.trackingNumber}
            </p>
            {order.deliveryStatus && (
              <p
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-secondary)',
                  marginTop: '2px',
                }}
              >
                {order.deliveryStatus}
              </p>
            )}
          </div>
        )}

        {/* Estimated completion */}
        {order.estimatedCompletionDate && (
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-tertiary)',
            }}
          >
            Est. completion:{' '}
            {new Date(order.estimatedCompletionDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </p>
        )}

        {/* Timeline */}
        {order.events.length > 1 && (
          <div style={{ marginTop: 'var(--space-1)' }}>
            <p
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: 'var(--space-2)',
              }}
            >
              Timeline
            </p>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-1)',
              }}
            >
              {order.events.map((event, i) => (
                <div
                  key={`${event.status}-${i}`}
                  className="flex justify-between"
                  style={{ fontSize: 'var(--text-xs)' }}
                >
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {STATUS_LABELS[event.status] ?? event.status}
                  </span>
                  <span style={{ color: 'var(--text-tertiary)' }}>
                    {new Date(event.timestamp).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const DetailItem = ({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}) => (
  <div>
    <p
      style={{
        fontSize: 'var(--text-xs)',
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      {label}
    </p>
    <p
      style={{
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-medium)',
        color: 'var(--text-primary)',
        marginTop: '2px',
      }}
    >
      {value}
    </p>
  </div>
);

const capitalize = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);
